/*
 * Purpose: Admin overview — quick stats + links to analytics/AI/engineer
 * Last modified: 2026-06-28 | Agent
 * Completeness: 92
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Bot, Shield, Wrench } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ContextualError } from '@/components/errors/ContextualError';
import { fetchAnalyticsSummary } from '@/lib/adminApi';
import { APP_ROUTES } from '@/lib/appRoutes';
import { PricingSubheadExperimentPanel } from '@/components/admin/PricingSubheadExperimentPanel';
import { useAuthStore } from '@/stores/authStore';
import { isSiteAdminEmail } from '@/lib/adminAccess';
import type { AnalyticsSummary } from '@/lib/siteAnalyticsTypes';

export function AdminOverviewPanel() {
  const email = useAuthStore((s) => s.email);
  const activeTenant = useAuthStore((s) => s.activeTenant);
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAnalyticsSummary(7)
      .then(setSummary)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load analytics'));
  }, []);

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Administrator</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Site analytics, AI recommendations, and Work Engineer remediation.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isSiteAdminEmail(email) && (
            <Badge variant="outline" className="gap-1">
              <Shield className="h-3 w-3" /> Site admin
            </Badge>
          )}
          <Badge variant="secondary">{activeTenant?.role ?? 'member'} · {activeTenant?.name}</Badge>
        </div>
      </div>

      {error && (
        <ContextualError
          message={error}
          action={{ label: 'Retry', onClick: () => window.location.reload() }}
          secondaryAction={{ label: 'System status', href: APP_ROUTES.systemStatus }}
        />
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Page views (7d)" value={summary?.totals.pageViews ?? '—'} />
        <StatCard label="Sessions (7d)" value={summary?.totals.sessions ?? '—'} />
        <StatCard label="Errors (7d)" value={summary?.totals.errors ?? '—'} />
        <StatCard label="Storage" value={summary?.storage ?? '—'} />
      </div>

      <PricingSubheadExperimentPanel />

      <div className="grid gap-3 sm:grid-cols-3">
        <QuickLink
          icon={BarChart3}
          title="Site analytics"
          description="Full traffic, users, paths, and error feed."
          href={APP_ROUTES.adminAnalytics}
        />
        <QuickLink
          icon={Bot}
          title="AI analyst"
          description="Interpret trends and get prioritized recommendations."
          href={APP_ROUTES.adminAiInsights}
        />
        <QuickLink
          icon={Wrench}
          title="Work Engineer"
          description="Diagnose errors and queue fix deployments."
          href={APP_ROUTES.adminEngineer}
        />
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{label}</CardDescription>
        <CardTitle className="text-2xl">{value}</CardTitle>
      </CardHeader>
    </Card>
  );
}

function QuickLink({
  icon: Icon,
  title,
  description,
  href,
}: {
  icon: typeof BarChart3;
  title: string;
  description: string;
  href: string;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-[hsl(var(--ab-green-core))]" />
          <CardTitle className="text-base">{title}</CardTitle>
        </div>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <Button size="sm" variant="outline" asChild>
          <Link to={href}>Open</Link>
        </Button>
      </CardContent>
    </Card>
  );
}
